import { useMemo } from "react";

import { useSplitPaymentFlow } from "~/hooks/use-split-payment-flow";
import { type PosProduct } from "~/types/product";
import {
  inferStockStatusFromBasket,
  type StockStatus
} from "~/utils/stock/infer-stock-status";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { shouldHideBarcodedProducts } from "~/utils/tills/should-hide-barcoded-products";

import { useBasketContext } from "../context";

export type DisplayProduct = PosProduct & {
  disabled: boolean;
  stockStatus: StockStatus;
};

export const useDisplayProducts = (): DisplayProduct[] => {
  const activeCategory = useBasketContext((state) => state.activeCategory);
  const products = useBasketContext((state) => state.products);
  const basket = useBasketContext((state) => state.basket);
  const userLimits = useBasketContext((state) => state.userLimits);

  const { data: shouldHideBarcoded } = useCurrentTill(
    shouldHideBarcodedProducts
  );
  const { isActive: isSplitPaymentActive } = useSplitPaymentFlow();

  const userLimitsMap = useMemo(
    () => new Map(userLimits.map((userLimit) => [userLimit.id, userLimit])),
    [userLimits]
  );

  const categoryProducts = useMemo(() => {
    const categoryId = activeCategory?.id;

    return products.filter((product) => {
      if (shouldHideBarcoded && product.barcodeIds.length > 0) {
        return false;
      }

      if (categoryId && categoryId !== "all") {
        if (categoryId === "popular") {
          return product.tags.includes("popular");
        }
        return product.categoryId === categoryId;
      }

      return true;
    });
  }, [products, activeCategory?.id, shouldHideBarcoded]);

  return useMemo(() => {
    return categoryProducts.map((product) => {
      let isDisabled = isSplitPaymentActive;
      if (product.userLimitId) {
        const userLimit = userLimitsMap.get(product.userLimitId);
        if (userLimit?.isLimitOver) {
          isDisabled = true;
        }
      }

      const stockStatus = inferStockStatusFromBasket({
        product,
        basket
      });

      isDisabled =
        isDisabled ||
        stockStatus === "reserved" ||
        stockStatus === "out-of-stock";

      return {
        ...product,
        disabled: isDisabled,
        stockStatus
      };
    });
  }, [categoryProducts, userLimitsMap, isSplitPaymentActive, basket]);
};
